import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpBaseService } from './http-base.service';
import { DocumentDetailDto, DocumentUploadRequest, DocumentListDto } from '../dtos/document.types';

@Injectable({
    providedIn: 'root'
})
export class DocumentService extends HttpBaseService {
    private readonly baseEndpoint = 'api/Documents';

    /**
     * Get all documents
     */
    getAllDocuments(): Observable<DocumentListDto[]> {
        return this.get<DocumentListDto[]>(`${this.baseEndpoint}`);
    }

    /**
     * Get document by ID
     */
    getDocumentById(documentId: string): Observable<DocumentDetailDto> {
        return this.get<DocumentDetailDto>(`${this.baseEndpoint}/${documentId}`);
    }

    /**
     * Download a document
     * @returns Observable with the document blob
     */
    downloadDocument(documentId: string): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/${this.baseEndpoint}/download/${documentId}`, {
            responseType: 'blob'
        });
    }

    /**
     * Download a specific page of a document as PNG
     * @param documentId - The document ID
     * @param pageNumber - The page number to download
     * @returns Observable with the page image blob
     */
    downloadDocumentPage(documentId: string, pageNumber: number): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/${this.baseEndpoint}/${documentId}/pages/${pageNumber}`, {
            responseType: 'blob'
        });
    }

    /**
     * Upload a document
     * @returns Observable containing the created document's ID
     */
    uploadDocument(uploadRequest: DocumentUploadRequest): Observable<{ id: string }> {
        const formData = new FormData();
        formData.append('file', uploadRequest.file);
        formData.append('NameFr', uploadRequest.nameFr || '');
        formData.append('NameEn', uploadRequest.nameEn || '');
        formData.append('DescriptionFr', uploadRequest.descriptionFr || '');
        formData.append('DescriptionEn', uploadRequest.descriptionEn || '');
        formData.append('ThematicId', uploadRequest.thematicId);

        return this.post<{ id: string }>(`${this.baseEndpoint}/upload`, formData);
    }

    /**
     * Update document status
     * @param documentId - The document ID
     * @param status - Boolean indicating the new status
     */
    updateDocumentStatus(documentId: string, status: boolean): Observable<void> {
        return this.patch<void>(`${this.baseEndpoint}/${documentId}/status`, status);
    }

    /**
     * Validate a document
     */
    validateDocument(documentId: string): Observable<void> {
        return this.patch<void>(`${this.baseEndpoint}/${documentId}/validate`);
    }

    /**
     * Delete a document
     */
    deleteDocument(documentId: string): Observable<void> {
        return this.delete<void>(`${this.baseEndpoint}/${documentId}`);
    }
}